'use client';

import type { VerificationTemplateProps } from './types';

function resolveLogoSrc(raw: string | null | undefined) {
    const value = (raw ?? '').trim();
    if (!value) return null;
    if (/^(https?:\/\/|data:)/.test(value)) return value;
    if (value.startsWith('/')) {
        const apiBase = (process.env.NEXT_PUBLIC_API_BASE || '').replace(/\/+$/, '');
        return apiBase ? `${apiBase}${value}` : value;
    }
    return value;
}

function ipfsToHttp(uri: string) {
    return uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
}

function isHexColor(value: string) {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

function normalizeHexColor(value: string) {
    const trimmed = value.trim();
    if (!isHexColor(trimmed)) return null;
    if (trimmed.length === 4) {
        const r = trimmed[1];
        const g = trimmed[2];
        const b = trimmed[3];
        return `#${r}${r}${g}${g}${b}${b}`;
    }
    return trimmed;
}

function darkenHex(hex: string, amount: number) {
    const normalized = normalizeHexColor(hex);
    if (!normalized) return null;

    const clamped = Math.min(1, Math.max(0, amount));
    const r = parseInt(normalized.slice(1, 3), 16);
    const g = parseInt(normalized.slice(3, 5), 16);
    const b = parseInt(normalized.slice(5, 7), 16);

    const darken = (channel: number) => {
        const next = Math.round(channel * (1 - clamped));
        return Math.min(255, Math.max(0, next));
    };

    const toHex = (channel: number) => channel.toString(16).padStart(2, '0');
    return `#${toHex(darken(r))}${toHex(darken(g))}${toHex(darken(b))}`;
}

function getHeroGradientBaseColor(config: unknown): string | null {
    if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
    const templateConfig = (config as Record<string, unknown>)['SWM_VIOLET'];
    if (!templateConfig || typeof templateConfig !== 'object' || Array.isArray(templateConfig)) return null;
    const backgroundColor = (templateConfig as Record<string, unknown>)['backgroundColor'];
    if (typeof backgroundColor !== 'string') return null;
    return normalizeHexColor(backgroundColor);
}

function getHeroGradientLogoUrl(config: unknown): string | null {
    if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
    const templateConfig = (config as Record<string, unknown>)['SWM_VIOLET'];
    if (!templateConfig || typeof templateConfig !== 'object' || Array.isArray(templateConfig)) return null;
    const logoUrl = (templateConfig as Record<string, unknown>)['logoUrl'];
    if (typeof logoUrl !== 'string' || !logoUrl.trim()) return null;
    return logoUrl.trim();
}

export default function TemplateHeroGradient({
    certificate,
    tenant,
    verificationCode,
}: VerificationTemplateProps) {
    const issuerDisplayName = tenant?.issuerName || tenant?.name || 'Sigalmark';
    // Fall back to OUR mark, not a tenant's: this used to point at
    // /swm_white_small.png (Sustainability West Midlands), so any tenant without
    // a logo showed another customer's branding to their recipients.
    // The with-text asset is white-on-transparent, which suits this template's
    // white-on-dark-gradient design.
    const SIGALMARK_LOGO = '/logo_with_text__theme_standard.png';
    const tenantLogo = resolveLogoSrc(tenant?.logoUrl);
    const logoSrc = tenantLogo || SIGALMARK_LOGO;

    const configuredBase = getHeroGradientBaseColor(tenant?.verificationTemplateConfig);
    const gradientLogoUrl = getHeroGradientLogoUrl(tenant?.verificationTemplateConfig);
    const base = configuredBase ?? '#502751';
    const edge = darkenHex(base, 0.35) ?? '#332131';

    return (
        <div
            className="min-h-screen w-full overflow-x-hidden text-white font-heading flex items-center justify-center"
            style={{
                backgroundImage:
                    `linear-gradient(to bottom, ${edge} 0%, ${base} 30%, ${base} 70%, ${edge} 100%)`,
            }}
        >
            <div className="w-full max-w-screen-xl p-8 flex flex-col items-center justify-center text-center space-y-12">
                <div className="w-full flex justify-center">
                    {(() => {
                        const src = gradientLogoUrl ? (resolveLogoSrc(gradientLogoUrl) || logoSrc) : logoSrc;
                        const isOwnMark = src === SIGALMARK_LOGO;
                        return (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                                src={src}
                                alt={isOwnMark ? 'Sigalmark' : `${issuerDisplayName} logo`}
                                className="w-64 h-auto mb-4"
                            />
                        );
                    })()}
                </div>

                <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-10 w-full max-w-4xl px-4 md:px-0 relative">
                    <div className="relative shrink-0">
                        {certificate.imageURI && (
                            <img
                                src={ipfsToHttp(certificate.imageURI)}
                                alt="Certificate NFT"
                                className="w-40 sm:w-48 h-auto rounded-md border shadow-md"
                            />
                        )}
                        {certificate.status === 'revoked' && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-destructive text-8xl font-bold">❌</span>
                            </div>
                        )}
                        {certificate.status === 'expired' && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-accent text-8xl font-bold">⏰</span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-5 md:space-y-7 text-center w-full md:w-auto flex flex-col items-center justify-center">
                        {certificate.status === 'revoked' ? (
                            <>
                                <p className="text-xl sm:text-2xl md:text-3xl text-destructive">This certificate has been revoked</p>
                                <p className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-destructive break-words max-w-full leading-tight">
                                    {certificate.name}
                                </p>
                                <p className="text-lg sm:text-xl md:text-2xl text-destructive">It is no longer valid</p>
                            </>
                        ) : certificate.status === 'expired' ? (
                            <>
                                <p className="text-xl sm:text-2xl md:text-3xl text-accent">This certificate has expired</p>
                                <p className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-accent break-words max-w-full leading-tight">
                                    {certificate.name}
                                </p>
                                <p className="text-lg sm:text-xl md:text-2xl text-accent">It is no longer valid</p>
                            </>
                        ) : (
                            <>
                                <p className="text-xl sm:text-2xl md:text-3xl">This registration mark confirms that</p>
                                <p className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl break-words max-w-full leading-tight">
                                    {certificate.name}
                                </p>
                                <p className="text-xl sm:text-2xl md:text-3xl">is a registered member of {issuerDisplayName}</p>
                            </>
                        )}
                    </div>
                </div>

                <div className="text-xl space-y-1 break-all">
                    <p className="pb-1">Verification number</p>
                    <span className="bg-card text-foreground px-2 py-1 rounded">{verificationCode}</span>
                    <p className="pt-2">
                        Powered by{' '}
                        <a
                            href="https://sigalmark.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="no-underline hover:no-underline text-accent hover:text-secondary"
                        >
                            sigalmark™
                        </a>
                    </p>
                    {certificate.explorerUrl && (
                        <p className="mt-12">
                            <a
                                href={certificate.explorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="no-underline hover:no-underline text-accent hover:text-secondary"
                            >
                                View on blockchain explorer
                            </a>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
